import { Copy, Download, FileUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcOutput } from '@pz/server';
import { fileText, readTextFile, saveFile } from '../lib/download';
import { d, lang } from '../lib/format';
import { useM } from '../lib/hooks';
import { Button, ErrorBox, Field, Modal, Notice, Pill, Textarea, th, td, useToast, type Tone } from './ui';
import { ScrollBox } from './virtual';

type Preview = ProcOutput<'approvals.importPending'>;

const ERROR_CODES = [
  'NOT_IN_QUEUE',
  'CONFLICT',
  'BAD_HOURS',
  'BAD_BILLING_TYPE',
  'PERIOD_LOCKED',
  'ENTRY_BILLED',
  'ENTRY_DELETED',
  'ENTRY_APPROVED',
  'SELF_APPROVAL',
  'WRONG_STATUS',
  'FORBIDDEN',
  'NOT_FOUND',
  'REASON_REQUIRED',
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

const FIELD_KEY = { hours: 'hours', description: 'description', billingType: 'billing_type' } as const;

/**
 * Hours that wait for my approval, edited in Excel: export as CSV, change hours / description / billing type, read
 * the file again. First a preview with every change and every problem, then apply. Each change goes through the same
 * rules as the edit dialog (rights, version, closed months) and is logged; the entries stay open for approval.
 * The file content is also shown as text, so it can be copied, pasted and edited without leaving the page.
 */
export function BulkHoursDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  const [exported, setExported] = useState<Parameters<typeof saveFile>[0] | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const exportM = useM('approvals.exportPending', {});
  const previewM = useM('approvals.importPending', {});
  const applyM = useM('approvals.importPending', { invalidate: ['approvals.', 'time.', 'kpi.', 'billing.'] });
  useEffect(() => {
    if (open) {
      setText('');
      setReason('');
      setExported(null);
      exportM.reset();
      previewM.reset();
      applyM.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result: Preview | undefined = applyM.data ?? previewM.data;
  const applied = !!applyM.data;
  const failed = new Map((applyM.data?.applyResult?.failed ?? []).map((f) => [f.id, f.code ?? 'OTHER']));
  const needsReason = !!previewM.data?.summary.needsReason;
  const reasonOk = !needsReason || reason.trim().length >= 3;

  const edit = (value: string) => {
    setText(value);
    previewM.reset();
    applyM.reset();
  };
  const doExport = () =>
    exportM.mutate(
      { lang: lang() },
      {
        onSuccess: (file) => {
          const csv = fileText(file);
          if (csv.trim().split('\n').length < 2) {
            toast.warn(t('approvals.bulk.noRows'));
            return;
          }
          setExported(file);
          edit(csv);
          toast.ok(t('approvals.bulk.exported', { count: csv.trim().split('\n').length - 1 }));
        },
        onError: (e) => toast.error(e),
      },
    );
  const pick = async (file: File | undefined) => {
    if (file) edit(await readTextFile(file));
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.ok(t('common.copied'));
    } catch {
      /* clipboard blocked (embedded view): the text can still be selected in the box */
    }
  };

  const errorText = (code: string | undefined) => t(`approvals.bulk.errors.${(ERROR_CODES as readonly string[]).includes(code ?? '') ? (code as ErrorCode) : 'OTHER'}`);
  const stateOf = (r: Preview['rows'][number]): { label: string; tone: Tone; detail?: string } => {
    const failure = failed.get(r.id);
    if (failure) return { label: t('approvals.bulk.stateError'), tone: 'bad', detail: errorText(failure) };
    if (r.status === 'error') return { label: t('approvals.bulk.stateError'), tone: 'bad', detail: errorText(r.error) };
    if (r.status === 'unchanged') return { label: t('approvals.bulk.stateUnchanged'), tone: 'neutral' };
    return { label: applied ? t('approvals.bulk.stateApplied') : t('approvals.bulk.stateChanged'), tone: applied ? 'good' : 'accent' };
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('approvals.bulk.title')}
      footer={
        applied ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button disabled={!text.trim()} loading={previewM.isPending} onClick={() => previewM.mutate({ csv: text, apply: false })}>
              {t('approvals.bulk.preview')}
            </Button>
            <Button
              variant="primary"
              disabled={!previewM.data || previewM.data.summary.changes === 0 || !reasonOk}
              loading={applyM.isPending}
              onClick={() =>
                applyM.mutate(
                  { csv: text, apply: true, reason: needsReason ? reason.trim() : null },
                  { onSuccess: (r) => toast.ok(t('approvals.bulk.applied', { count: r.applyResult?.done ?? 0 })), onError: (e) => toast.error(e) },
                )
              }
            >
              {t('approvals.bulk.apply')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-graphite">{t('approvals.bulk.intro')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <Button icon={<Download className="size-4" />} loading={exportM.isPending} onClick={doExport}>
            {t('approvals.bulk.export')}
          </Button>
          {exported && (
            <Button variant="ghost" onClick={() => saveFile(exported)}>
              {t('approvals.bulk.save')}
            </Button>
          )}
          <span className="mx-1 hidden h-5 w-px bg-line sm:block" aria-hidden />
          <span className="text-sm font-medium">{t('approvals.bulk.import')}</span>
          <input ref={picker} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
          <Button icon={<FileUp className="size-4" />} onClick={() => picker.current?.click()}>
            {t('approvals.bulk.chooseFile')}
          </Button>
        </div>

        <Field label={text ? t('approvals.bulk.fileText') : t('approvals.bulk.orPaste')}>
          <Textarea rows={text ? 7 : 4} wrap="off" spellCheck={false} className="font-mono text-xs" value={text} onChange={(e) => edit(e.target.value)} />
        </Field>
        {text && (
          <div className="-mt-2 flex">
            <Button size="sm" variant="ghost" icon={<Copy className="size-3.5" />} onClick={() => void copy()}>
              {t('approvals.bulk.copy')}
            </Button>
          </div>
        )}

        <ErrorBox error={exportM.error ?? previewM.error ?? applyM.error} />

        {result && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone="neutral">{t('approvals.bulk.summary', { changed: result.summary.changes, unchanged: result.summary.unchanged, errors: result.summary.errors })}</Pill>
            </div>
            {!applied && <Notice tone="info">{t('approvals.bulk.previewOnly')}</Notice>}
            {applied && <Notice tone={failed.size ? 'warn' : 'good'}>{t('approvals.bulk.applied', { count: applyM.data?.applyResult?.done ?? 0 })}</Notice>}
            {needsReason && !applied && (
              <Field label={t('common.reason')} hint={t('common.reasonHint')}>
                <Textarea rows={2} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
              </Field>
            )}
            <ScrollBox className="rounded-md border border-line" maxHeight="min(40vh, 22rem)" label={t('approvals.bulk.preview')}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-line bg-sheet-2">
                  <tr>
                    <th className={th}>{t('approvals.bulk.line')}</th>
                    <th className={th}>{t('approvals.bulk.person')}</th>
                    <th className={th}>{t('approvals.bulk.date')}</th>
                    <th className={th}>{t('approvals.bulk.change')}</th>
                    <th className={th}>{t('approvals.bulk.state')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => {
                    const st = stateOf(r);
                    return (
                      <tr key={`${r.line}:${r.id}`} className="border-b border-line/60 last:border-0">
                        <td className={`${td} num text-graphite`}>{r.line}</td>
                        <td className={td}>
                          <span className="block">{r.person}</span>
                          <span className="block text-xs text-graphite">{[r.project, r.task].filter(Boolean).join(' · ')}</span>
                        </td>
                        <td className={`${td} num`}>{r.date ? d(r.date) : ''}</td>
                        <td className={td}>
                          {r.changes.map((c) => (
                            <span key={c.field} className="block">
                              <span className="text-graphite">{t(`approvals.bulk.fields.${FIELD_KEY[c.field]}`)}: </span>
                              {c.from || '–'} → <b className="font-medium">{c.to || '–'}</b>
                            </span>
                          ))}
                        </td>
                        <td className={td}>
                          <Pill tone={st.tone}>{st.label}</Pill>
                          {st.detail && <span className="mt-0.5 block text-xs text-bad">{st.detail}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollBox>
          </div>
        )}
      </div>
    </Modal>
  );
}
